import { WSList, WSPanel, WSText } from "@wingspanhq/fe-component-library";
import { useMemo } from "react";
import { FlowMEChangeContext } from "..";
import { queriesPayerRows, queriesPersons } from "@query";
import { useSession } from "@hooks/useSession";
import { useQueryPrimaryComplianceEntity } from "../../../../../shared/hooks/useQueryPrimaryComplianceEntity";
import { useFlowContext } from "../../../../../shared/components/Flow";
import { verificationService } from "../../../../../shared/services/verificaiton";
import { FormBusinessInformation } from "../../../components/FormBusinessInformation";
import { getProfileDataBusinessV3 } from "../../../components/FormBusinessInformation/getProfileDataBusinessV3";
import { useRequiresBankingVerification } from "../../../hooks/useRequiresBankingVerification";
import { useMissingFields } from "../../../hooks/useMissingFields";

import { FlowStep } from "../../../../../shared/components/FlowSwitch/types";

export const StepMaterialUpdateBusinessInformation: React.FC<
  FlowStep
> = props => {
  const [context, setContext] = useFlowContext<FlowMEChangeContext>();
  const requiresBanking = useRequiresBankingVerification();
  const verificationLevel = requiresBanking ? "Banking" : "Tax";

  const session = useSession();
  const queryPerson = queriesPersons.getPerson.useQuery({
    personId: session.personId
  });
  const queryComplianceEntity = useQueryPrimaryComplianceEntity();
  const profileData = useMemo(
    () => getProfileDataBusinessV3(queryComplianceEntity.data),
    [queryComplianceEntity.data]
  );

  const missingFields = useMissingFields(verificationLevel);
  const isMismatch = useMemo(
    () => verificationService.getIsBusinessMismatch(missingFields),
    [missingFields]
  );

  const queryPayers = queriesPayerRows.searchPayerRows.usePaginatedQuery({
    page: { size: 1 }
  });
  const payerCompanyName = useMemo(() => {
    const payer = queryPayers.flatData?.[0];

    if (!payer) {
      return undefined;
    }

    // The v3 row carries one company name pair; v1 read them off the embedded member.
    return {
      doingBusinessAs: payer.profile?.doingBusinessAs || undefined,
      legalBusinessName: payer.profile?.companyName || undefined
    };
  }, [queryPayers.flatData]);

  // These forms compute their defaults once, at mount, so the read the prefill
  // comes from has to have settled first — otherwise the fields stay empty.
  if (!queryComplianceEntity.isFetched) {
    return <WSPanel shimmer />;
  }

  return (
    <WSList gap="2XL">
      <FormBusinessInformation
        country={context.accountType?.country || "US"}
        defaultValues={context.businessInformation || profileData}
        onBack={props.onBack}
        onSubmit={data => {
          setContext(prev => ({ ...prev, businessInformation: data }));
          props.onNext?.();
        }}
        show1099Message
        personalEmail={queryPerson.data?.email || undefined}
        payerCompanyName={payerCompanyName}
        mismatch={isMismatch}
      />

      <WSText.ParagraphXs color="gray400">
        The information on this page is required to setup your Wingspan account,
        if you exit or leave without completing this step, you will return to
        this page the next time you sign-in
      </WSText.ParagraphXs>
    </WSList>
  );
};
